import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import LocalizedLink from "@/components/LocalizedLink";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import { useAuthStore } from "@/store";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function RegisterPage() {
  const navigate = useLocalizedNavigation();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    const result = await register({ name, email, password });
    if (result.success) {
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } else {
      toast.error(result.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md">
          <LocalizedLink to="/" className="flex items-center gap-2 mb-8">
            <img 
              src="/assets/logo-creavideos.png" 
              alt="CreaVideos" 
              className="h-10 w-auto"
            />
          </LocalizedLink>

          <h1 className="text-3xl font-bold mb-2">Create your account</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="register-terms"
              />
              <Label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
                I agree to the{" "}
                <LocalizedLink to="/terms-of-service" className="text-teal-600 hover:text-teal-700">Terms of Service</LocalizedLink>
                {" "}and{" "}
                <LocalizedLink to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</LocalizedLink>
              </Label>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 btn-primary rounded-full"
              disabled={isLoading}
              data-testid="register-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create account"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-600">
            Already have an account?{" "}
            <LocalizedLink to="/login" className="text-teal-600 font-medium hover:text-teal-700">
              Log in
            </LocalizedLink>
          </p>
      </div>
    </div>
  );
}
